import { InstructionType } from '../Instruction';
import { Mesh, AnimationMixer, Vector3 } from 'three';
import { Direction, dirForward, dirOpposite, dirRotation, dirTurn, Turn } from '../Direction';
import { Anim } from '../Animation';
import { Assets } from '../Assets';
import { SkeletonUtils } from '../../utils/skeleton-utils';
import { LevelObject } from '../LevelObject';
import { mapObject } from '../../utils/object-tools';
import { Obstacle } from '../components/Obstacle';
import { isGrabbable } from '../components/Grabbable';
import { Updater } from '../components/Updater';
import { isStackable } from '../components/Stackable';
import { GameError } from '../GameError';
export class Robot extends Updater(Obstacle(LevelObject)) {
    constructor(name, levelMap, position) {
        super(levelMap, SkeletonUtils.clone(Assets.treadBot), position);
        this.name = name;
        this.direction = Direction.South;
        this.holding = undefined;
        this.rightHand = this.model.getObjectByName('Hand_R');
        this.mixer = new AnimationMixer(this.model);
        this.actions = mapObject(Assets.treadBotAnimations, (clip) => this.mixer.clipAction(clip));
        this.actions.Grab.timeScale = 2;
        this.actions.Grab.repetitions = 1;
        this.instructions = [];
    }
    get type() {
        return 'robot';
    }
    getProperties() {
        return Object.assign(Object.assign({}, super.getProperties()), { name: this.name, can_move: true });
    }
    toApiJson() {
        return {
            x: this.tilePos.x,
            y: this.tilePos.y,
            direction: this.direction,
        };
    }
    enqueue(instruction) {
        this.instructions.push(instruction);
    }
    isActive() {
        return !!this.current || this.instructions.length > 0;
    }
    setHatColor(color) {
        this.model.traverse(object => {
            if (object instanceof Mesh && object.material.name === 'Hat') {
                window.orig = object.material;
                const materialCopy = object.material.clone();
                materialCopy.color = color;
                object.material = materialCopy;
            }
        });
    }
    /**
     * Called immediate before startstep, use this to clean up any leftovers that may have been setup
     * during the previous step. This is guaranteed to be called before *any* robots startStep, so it's
     * good for clearing reserved resources before robots start next step.
     */
    endStep(step) {
        // If running an instruction now, should it end?
        if (this.current) {
            const endStep = this.current.startStep + Robot.instructionDuration(this.current.instruction) - 1;
            if (step >= endStep) {
                this.endCurrentInstruction();
            }
            else {
                this.current.animation = undefined;
            }
        }
    }
    startStep(step) {
        // If no instruction is running, grab the next one.
        if (!this.current && this.instructions.length > 0) {
            this.current = {
                instruction: this.instructions.shift(),
                startStep: step,
            };
            if (this.current.instruction.type === InstructionType.walk) {
                this.actions.Move.play();
            }
            else if (this.current.instruction.type === InstructionType.grab) {
                this.actions.Grab.reset();
                this.actions.Grab.play();
            }
            else {
                this.actions.Idle.play();
            }
        }
        // If an instruction is running, check if it needs to process anything.
        if (this.current) {
            this.instructionStartStep(step - this.current.startStep);
        }
    }
    update(step, duration) {
        this.mixer.update(duration / 1000);
        if (this.current) {
            this.instructionUpdateStep(step - this.current.startStep);
        }
    }
    static instructionDuration(instruction) {
        if ([InstructionType.walk, InstructionType.turn].includes(instruction.type)) {
            return Math.abs(instruction.arg('steps', 1));
        }
        else if (instruction.type === InstructionType.wait) {
            return Math.abs(instruction.arg('time', 1));
        }
        else {
            return 1;
        }
    }
    worldRotation() {
        return dirRotation(this.direction);
    }
    instructionStartStep(relativeStep) {
        switch (this.current.instruction.type) {
            case InstructionType.walk: {
                const backwards = this.current.instruction.arg('steps', 1) < 0;
                const dir = backwards ? dirOpposite(this.direction) : this.direction;
                const target = this.tilePos.clone().add(dirForward(dir));
                if (this.moveToTile(target)) {
                    this.current.animation = new Anim(relativeStep, this.model.position, this.model.quaternion);
                    this.current.animation.slideTo(this.levelMap.tileToWorld(target));
                }
                break;
            }
            case InstructionType.turn: {
                const turn = this.current.instruction.arg('steps', 1) < 0 ? Turn.Left : Turn.Right;
                const target = dirTurn(this.direction, turn);
                this.current.animation = new Anim(relativeStep, this.model.position, this.model.quaternion);
                this.current.animation.turnTo(dirRotation(target));
                this.direction = target;
                break;
            }
            case InstructionType.grab: {
                if (this.holding && !isStackable(this.holding)) {
                    throw new GameError(`Robot "${this.name}" is already holding something.`, "Robots can only hold one thing at a time. You tried to <code>GRAB</code> something while your robot's hands were already full.");
                }
                const target = this.tilePos.clone().add(dirForward(this.direction));
                const objects = this.levelMap.getObjectsAt(target);
                for (const o of objects) {
                    if (isGrabbable(o)) {
                        if (this.holding && isStackable(this.holding)) {
                            if (this.holding.stack(o)) {
                                break;
                            }
                        }
                        else {
                            const grabbed = o.grab(this.current.instruction.arg('what', undefined));
                            if (grabbed) {
                                grabbed.model.parent.remove(grabbed.model);
                                grabbed.position = new Vector3();
                                this.rightHand.add(grabbed.model);
                                this.holding = grabbed;
                                break;
                            }
                        }
                    }
                }
                break;
            }
            case InstructionType.drop: {
                if (!this.holding) {
                    throw new GameError(`Robot "${this.name}" is not holding anything.`, "You asked a Robot to <code>DROP</code> something, but it was not holding anything.");
                }
                const target = this.tilePos.clone().add(dirForward(this.direction));
                if (this.holding.drop(target)) {
                    this.holding = null;
                }
                else {
                    throw new GameError(`Robot "${this.name}" can't drop an item there.`, "You asked a Robot to <code>DROP</code> something, but there is something in the way.");
                }
            }
        }
    }
    instructionUpdateStep(relativeStep) {
        if (this.current.animation) {
            this.current.animation.apply(this.model, relativeStep);
        }
    }
    endCurrentInstruction() {
        // End current animation / movement.
        this.instructionUpdateStep(Robot.instructionDuration(this.current.instruction));
        this.current = undefined;
    }
}
